import { useMemo } from 'react';
import {
  Activity,
  BookOpen,
  ChevronRight,
  Database,
  Map,
  MessageCircle,
  Settings,
  ShoppingBag,
  Users,
  Zap,
} from 'lucide-react';
import { useScope } from '@workspace/scope-context';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useNavigation } from '@/context/NavigationContext';
import { useApiGatewayAccess } from '@/lib/useApiGatewayAccess';
import { useCommerceAccess } from '@/lib/useCommerceAccess';
import { useMappingAccess } from '@/lib/useMappingAccess';
import { useNotificationAccess } from '@/lib/useNotificationAccess';
import { useStatusInventoryAccess } from '@/lib/useStatusInventoryAccess';
import { useTenantUsersAccess } from '@/lib/useTenantUsersAccess';

type WorkspaceCard = {
  id: string;
  title: string;
  description: string;
  route: string;
  icon: React.ComponentType<{ className?: string }>;
};

// Per-workspace accent colors — icon bg + icon text, light and dark
const WORKSPACE_COLORS: Record<string, { bg: string; text: string }> = {
  commerce: {
    bg: 'bg-amber-50 dark:bg-amber-950/30',
    text: 'text-amber-600 dark:text-amber-400',
  },
  maps: {
    bg: 'bg-emerald-50 dark:bg-emerald-950/30',
    text: 'text-emerald-600 dark:text-emerald-400',
  },
  sales: {
    bg: 'bg-teal-50 dark:bg-teal-950/30',
    text: 'text-teal-600 dark:text-teal-400',
  },
  status: {
    bg: 'bg-blue-50 dark:bg-blue-950/30',
    text: 'text-blue-600 dark:text-blue-400',
  },
  notifications: {
    bg: 'bg-violet-50 dark:bg-violet-950/30',
    text: 'text-violet-600 dark:text-violet-400',
  },
  users: {
    bg: 'bg-rose-50 dark:bg-rose-950/30',
    text: 'text-rose-600 dark:text-rose-400',
  },
  automation: {
    bg: 'bg-slate-100 dark:bg-slate-800/40',
    text: 'text-slate-600 dark:text-slate-400',
  },
};

const DEFAULT_COLORS = { bg: 'bg-muted/50', text: 'text-muted-foreground' };

export default function PortalHomePage() {
  const navigation = useNavigation();
  const { activeTenant } = useScope();
  const mappingAccess = useMappingAccess();
  const commerceAccess = useCommerceAccess();
  const apiGatewayAccess = useApiGatewayAccess();
  const notificationAccess = useNotificationAccess();
  const statusInventoryAccess = useStatusInventoryAccess();
  const tenantUsersAccess = useTenantUsersAccess();

  const workspaceCards = useMemo<WorkspaceCard[]>(() => {
    const cards: WorkspaceCard[] = [];

    if (commerceAccess.commerceFeatureEnabled || commerceAccess.resellerFeatureEnabled) {
      cards.push({
        id: 'commerce',
        title: commerceAccess.commerceFeatureEnabled ? 'Commerce' : 'Agent commerce',
        description: commerceAccess.commerceFeatureEnabled
          ? 'Catalogs, markets, storefronts, orders, and pricing for the active tenant.'
          : 'Reseller orders, carts, entitlements, and commission workflows.',
        route: commerceAccess.commerceFeatureEnabled ? '/commerce/catalogs' : '/reseller/orders',
        icon: ShoppingBag,
      });
    }

    if (mappingAccess.mappingFeatureEnabled) {
      cards.push({
        id: 'maps',
        title: 'Maps',
        description: 'Manage tenant-scoped map sources, styles, custom maps, sprites, and glyphs.',
        route: '/maps/sources',
        icon: Map,
      });
    }

    if (mappingAccess.featureMap?.['sales.runtime.enabled']) {
      cards.push({
        id: 'sales',
        title: 'Sales',
        description: 'Open the tenant sales map and related geographic workflows.',
        route: '/sales',
        icon: Database,
      });
    }

    if (statusInventoryAccess.accessEnabled) {
      cards.push({
        id: 'status',
        title: 'Status',
        description: 'Inspect inventory, links, models, overlays, and SNMP-related records.',
        route: '/status/inventory/hubs',
        icon: Activity,
      });
    }

    if (notificationAccess.accessEnabled) {
      cards.push({
        id: 'notifications',
        title: 'Notifications',
        description: 'Configure delivery channels, recipients, routes, and activity history.',
        route: '/notifications/channels',
        icon: Zap,
      });
    }

    if (tenantUsersAccess.accessEnabled) {
      cards.push({
        id: 'users',
        title: 'Users',
        description: 'Manage active users and invite links for the current tenant.',
        route: '/users/active',
        icon: Users,
      });
    }

    if (apiGatewayAccess.apiGatewayFeatureEnabled) {
      cards.push({
        id: 'automation',
        title: 'API access',
        description: 'Open API gateway access and developer console workflows for this tenant.',
        route: '/api-gateway/access',
        icon: Settings,
      });
    }

    return cards;
  }, [
    apiGatewayAccess.apiGatewayFeatureEnabled,
    commerceAccess.commerceFeatureEnabled,
    commerceAccess.resellerFeatureEnabled,
    mappingAccess.featureMap,
    mappingAccess.mappingFeatureEnabled,
    notificationAccess.accessEnabled,
    statusInventoryAccess.accessEnabled,
    tenantUsersAccess.accessEnabled,
  ]);

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-5 px-4 py-5 md:px-6 md:py-6">
      {/* ── Slim hero ────────────────────────────────────────── */}
      <div className="rounded-xl border border-border/60 bg-card p-4 md:p-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground/60">
          Tenant portal
        </p>
        <h1 className="mt-1.5 text-2xl font-black tracking-tight text-foreground md:text-3xl">
          {activeTenant?.name ? `Welcome to ${activeTenant.name}` : 'Welcome to the portal'}
        </h1>
        <p className="mt-1.5 text-sm text-muted-foreground">
          Select a workspace below to get started.
        </p>
      </div>

      {/* ── Available workspaces ─────────────────────────────── */}
      <section className="space-y-3">
        <h2 className="text-sm font-bold tracking-tight text-foreground">
          Available workspaces
        </h2>

        {workspaceCards.length === 0 ? (
          <EmptyWorkspacesState
            onSetup={() => navigation.navigate('/setup')}
            onSupport={() => navigation.navigate('/support')}
          />
        ) : (
          <div className="grid gap-2.5 sm:grid-cols-2 xl:grid-cols-3">
            {workspaceCards.map(card => (
              <WorkspaceCardItem
                key={card.id}
                card={card}
                onClick={() => navigation.navigate(card.route)}
              />
            ))}
          </div>
        )}
      </section>

      {/* ── Support / help strip ─────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border/50 bg-card/60 px-4 py-3">
        <span className="flex-1 text-xs text-muted-foreground">Need help or have a question?</span>
        <Button
          size="sm"
          variant="outline"
          onClick={() => navigation.navigate('/support')}
          className="h-7 gap-1.5 px-3 text-xs"
        >
          <MessageCircle className="h-3.5 w-3.5" aria-hidden />
          Support
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => navigation.navigate('/help')}
          className="h-7 gap-1.5 px-3 text-xs"
        >
          <BookOpen className="h-3.5 w-3.5" aria-hidden />
          Help center
        </Button>
      </div>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────

function WorkspaceCardItem({
  card,
  onClick,
}: {
  card: WorkspaceCard;
  onClick: () => void;
}) {
  const Icon = card.icon;
  const colors = WORKSPACE_COLORS[card.id] ?? DEFAULT_COLORS;

  return (
    <div
      role="link"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={e => e.key === 'Enter' && onClick()}
      className="group cursor-pointer rounded-xl border border-border/60 bg-card transition-all hover:border-border hover:shadow-sm active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {/* Mobile: horizontal list-item */}
      <div className="flex items-center gap-3 p-3.5 sm:hidden">
        <div
          className={cn(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
            colors.bg
          )}
        >
          <Icon className={cn('h-4.5 w-4.5', colors.text)} aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold leading-tight text-foreground">{card.title}</p>
          <p className="mt-0.5 truncate text-xs text-muted-foreground">{card.description}</p>
        </div>
        <ChevronRight
          className="h-4 w-4 shrink-0 text-muted-foreground/40 transition-colors group-hover:text-muted-foreground"
          aria-hidden
        />
      </div>

      {/* Desktop: vertical card */}
      <div className="hidden flex-col gap-3 p-4 sm:flex">
        <div
          className={cn(
            'flex h-9 w-9 items-center justify-center rounded-lg',
            colors.bg
          )}
        >
          <Icon className={cn('h-4 w-4', colors.text)} aria-hidden />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-foreground">{card.title}</h3>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">{card.description}</p>
        </div>
        <span className="flex items-center gap-1 text-xs font-medium text-muted-foreground/70 transition-colors group-hover:text-foreground">
          Open {card.title}
          <ChevronRight className="h-3.5 w-3.5" aria-hidden />
        </span>
      </div>
    </div>
  );
}

function EmptyWorkspacesState({
  onSetup,
  onSupport,
}: {
  onSetup: () => void;
  onSupport: () => void;
}) {
  return (
    <div className="flex flex-col items-center gap-4 rounded-xl border border-border/60 bg-card/60 px-6 py-10 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-muted/60">
        <Zap className="h-7 w-7 text-muted-foreground/50" aria-hidden />
      </div>
      <div className="space-y-1">
        <p className="text-sm font-semibold text-foreground">No workspaces available yet</p>
        <p className="max-w-xs text-xs leading-5 text-muted-foreground">
          Select a tenant or complete setup to unlock available workspaces for this session.
        </p>
      </div>
      <div className="flex flex-wrap justify-center gap-2">
        <Button size="sm" onClick={onSetup}>
          Continue setup
        </Button>
        <Button size="sm" variant="outline" onClick={onSupport}>
          Open support
        </Button>
      </div>
    </div>
  );
}
